import * as React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
// removed ungrouped radio group import -- we'll load countries/regions instead
import { useState, useEffect } from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import api from "../api.js";
import FormLabel from "@mui/material/FormLabel";
import Switch from "@mui/material/Switch";
import Box from "@mui/material/Box";
import NumberSpinner from "./NumberSpinner";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

export default function CustomizedDialogsFilter({
  openFilterDialog,
  setOpenFilterDialog,
  applyFilters,
  filterMessage,
  chosenRegion,
  showManyTracks,
  highPagination,
  startYear,
  endYear,
  filterByYear,
  filterByRegion,
}) {
  // const handleClickOpen = () => {
  //   setOpen(true);
  // };

  // console.log("open =", open);

  const [checkedSwitch1, setCheckedSwitch1] = React.useState(showManyTracks);
  const [checkedSwitch2, setCheckedSwitch2] = React.useState(highPagination);
  const [checkedSwitch3, setCheckedSwitch3] = React.useState(filterByYear);
  const [checkedSwitch4, setCheckedSwitch4] = React.useState(filterByRegion);

  // nested country-region data
  const [countriesMap, setCountriesMap] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [selectedCountryLocal, setSelectedCountryLocal] = useState("");
  const [regionsForCountry, setRegionsForCountry] = useState([]);
  const [selectedRegionLocal, setSelectedRegionLocal] = useState(
    chosenRegion || "",
  );

  // fetch mapping when dialog opens
  useEffect(() => {
    if (!openFilterDialog) return;
    api
      .get("/api/country?populate=*")
      .then((res) => {
        // Strapi might wrap in data or not
        const attributes = res?.data?.data?.attributes || res?.data?.attributes;
        const map = attributes?.country || attributes?.Country || {};

        if (!map || typeof map !== "object") {
          console.warn("unexpected region map format", res.data);
          return;
        }
        setCountriesMap(map);
        const countries = Object.keys(map).sort();
        setCountryList(countries);
        // determine initial country based on chosenRegion
        let initialCountry = "";
        for (const c of countries) {
          const list = map[c] || [];
          if (list.includes(chosenRegion)) {
            initialCountry = c;
            break;
          }
        }
        if (!initialCountry && countries.length) {
          initialCountry = countries[0];
        }
        setSelectedCountryLocal(initialCountry);
        if (
          initialCountry &&
          Array.isArray(map[initialCountry]) &&
          map[initialCountry].length === 0
        ) {
          // for countries with no subregions, treat the country as the region
          setSelectedRegionLocal(initialCountry);
        }
      })
      .catch((err) => {
        console.error("Failed to load region map", err);
      });
  }, [openFilterDialog, chosenRegion]);

  // update region list when country changes
  useEffect(() => {
    if (selectedCountryLocal && countriesMap[selectedCountryLocal]) {
      const regions = countriesMap[selectedCountryLocal];
      setRegionsForCountry(regions);
      if (regions.length === 0) {
        setSelectedRegionLocal(selectedCountryLocal);
      } else if (!regions.includes(selectedRegionLocal)) {
        setSelectedRegionLocal(regions[0] || "");
      }
    }
  }, [selectedCountryLocal, countriesMap]);

  const handleCancel = () => {
    setOpenFilterDialog(false);
  };

  const handleConfirm = async () => {
    if (!applyFilters) {
      setOpenFilterDialog(false);
      return;
    }

    const success = await applyFilters({
      chosenRegion: selectedRegionLocal,
      startYear: localStartYear,
      endYear: localEndYear,
      filterByYear: checkedSwitch3,
      filterByRegion: checkedSwitch4,
      showManyTracks: checkedSwitch1,
      highPagination: checkedSwitch2,
    });

    if (success) {
      setOpenFilterDialog(false);
    }
  };

  const handleChangeSwitch1 = (event) => {
    setCheckedSwitch1(event.target.checked);
  };

  const handleChangeSwitch2 = (event) => {
    setCheckedSwitch2(event.target.checked);
  };

  const handleChangeSwitch3 = (event) => {
    setCheckedSwitch3(event.target.checked);
  };

  const handleChangeSwitch4 = (event) => {
    const checked = event.target.checked;
    setCheckedSwitch4(checked);
    if (!checked) {
      setSelectedCountryLocal("");
      setSelectedRegionLocal("");
    } else if (!selectedCountryLocal && countryList.length > 0) {
      setSelectedCountryLocal(countryList[0]);
    }
  };

  // Local state for spinners
  const [localStartYear, setLocalStartYear] = useState(startYear || 2018);
  const [localEndYear, setLocalEndYear] = useState(endYear || 2025);

  useEffect(() => {
    if (!openFilterDialog) return;

    setCheckedSwitch1(showManyTracks);
    setCheckedSwitch2(highPagination);
    setCheckedSwitch3(filterByYear);
    setCheckedSwitch4(filterByRegion);
    setLocalStartYear(startYear || 2018);
    setLocalEndYear(endYear || 2025);
    setSelectedRegionLocal(chosenRegion || "");
  }, [
    openFilterDialog,
    showManyTracks,
    highPagination,
    filterByYear,
    filterByRegion,
    startYear,
    endYear,
    chosenRegion,
  ]);

  return (
    <div>
      {/* <Button
        variant="outlined"
        onClick={handleClickOpen}
        sx={{ color: "white" }}
        class="css-sghohy-MuiButtonBase-root-MuiButton-root"
      >
        GPX
      </Button> */}

      <BootstrapDialog
        onClose={handleCancel}
        aria-labelledby="customized-dialog-title"
        open={openFilterDialog}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleCancel}
        >
          FILTER
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <FormLabel id="demo-radio-buttons-group-label">
            Filter by region: no/yes
          </FormLabel>
          <Switch
            checked={checkedSwitch4}
            onChange={handleChangeSwitch4}
            inputProps={{ "aria-label": "controlled" }}
          />
          <DialogContent dividers></DialogContent>

          {checkedSwitch4 ? (
            <FormControl sx={{ display: "flex", flexDirection: "column" }}>
              <FormLabel>Select country</FormLabel>
              <RadioGroup
                value={selectedCountryLocal}
                onChange={(e) => setSelectedCountryLocal(e.target.value)}
                sx={{ flexDirection: "column" }}
              >
                {countryList.map((c) => (
                  <Box
                    key={c}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <FormControlLabel value={c} control={<Radio />} label={c} />
                    {c === selectedCountryLocal &&
                      regionsForCountry.length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            ml: 3,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <RadioGroup
                            value={selectedRegionLocal}
                            onChange={(e) =>
                              setSelectedRegionLocal(e.target.value)
                            }
                            sx={{ flexDirection: "column" }}
                          >
                            {regionsForCountry.map((r) => (
                              <FormControlLabel
                                key={r}
                                value={r}
                                control={<Radio />}
                                label={r}
                              />
                            ))}
                          </RadioGroup>
                        </Box>
                      )}
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>
          ) : null}

          <DialogContent dividers></DialogContent>
          <FormLabel id="demo-radio-buttons-group-label">
            Tracks on map: one/many
          </FormLabel>

          <Switch
            checked={checkedSwitch1}
            onChange={handleChangeSwitch1}
            inputProps={{ "aria-label": "controlled" }}
          />
          <DialogContent dividers></DialogContent>
          <FormLabel id="demo-radio-buttons-group-label">
            Pagination: low/heigh
          </FormLabel>
          <Switch
            checked={checkedSwitch2}
            onChange={handleChangeSwitch2}
            inputProps={{ "aria-label": "controlled" }}
          />
          <DialogContent dividers></DialogContent>

          <FormLabel id="demo-radio-buttons-group-label">
            Filter by year: no/yes
          </FormLabel>

          <Switch
            checked={checkedSwitch3}
            onChange={handleChangeSwitch3}
            inputProps={{ "aria-label": "controlled" }}
          />

          {checkedSwitch3 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                alignItems: "center",
              }}
            >
              <NumberSpinner
                label="Select start year"
                min={2000}
                max={2100}
                value={localStartYear}
                onValueChange={(val) =>
                  setLocalStartYear(Math.max(2000, Math.min(2100, Number(val))))
                }
                size="small"
              />
              <NumberSpinner
                label="Select end year"
                min={2000}
                max={2100}
                value={localEndYear}
                onValueChange={(val) =>
                  setLocalEndYear(Math.max(2000, Math.min(2100, Number(val))))
                }
                size="small"
              />
            </Box>
          ) : null}
        </DialogContent>

        {filterMessage ? (
          <Typography variant="body2" color="error" sx={{ mt: 2, ml: 2 }}>
            {filterMessage}
          </Typography>
        ) : null}
        <DialogActions>
          <Button autoFocus onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
}
